import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import {
    FaCut,
    FaPaintBrush,
    FaStar,
    FaCrown,
    FaTint,
    FaHome,
    FaClock,
    FaCheckCircle,
    FaDollarSign,
    FaLock,
    FaChevronRight,
    FaQuoteLeft,
    FaCamera,
    FaCalendarAlt,
    FaTimes,
    FaChevronLeft,
    FaChevronRight as FaArrowRight
} from 'react-icons/fa';
import { BsWhatsapp } from 'react-icons/bs';
import Layout from '../../components/Layout/Layout';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import styles from './styles/HomePage.module.css';

export default function HomePage() {
    // Fonction pour scroller vers une section
    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const [activeTab, setActiveTab] = useState('all');
    const [selectedImageId, setSelectedImageId] = useState(null);

    const services = [
        {
            id: 1,
            icon: FaCut,
            name: 'Coupe & Mise en Forme',
            description: 'Coupe personnalisée adaptée à votre morphologie et style',
            price: '25€',
            duration: '45 min',
            category: 'coupe'
        },
        {
            id: 2,
            icon: FaPaintBrush,
            name: 'Coloration Complète',
            description: 'Couleurs éclatantes avec produits premium',
            price: '50€',
            duration: '2h',
            category: 'couleur'
        },
        {
            id: 3,
            icon: FaStar,
            name: 'Balayage & Mèches',
            description: 'Effet naturel et lumineux sur vos cheveux',
            price: '60€',
            duration: '2h30',
            category: 'couleur'
        },
        {
            id: 4,
            icon: FaTint,
            name: 'Soins Capillaires',
            description: 'Traitement intensif pour cheveux sains et brillants',
            price: '40€',
            duration: '1h',
            category: 'soin'
        },
        {
            id: 5,
            icon: FaCrown,
            name: 'Coiffure Événementielle',
            description: 'Coiffure d\'exception pour vos grands moments',
            price: '75€',
            duration: '1h30',
            category: 'event'
        },
        {
            id: 6,
            icon: FaStar,
            name: 'Lissage Brésilien',
            description: 'Cheveux lisses et soyeux pendant 3 mois',
            price: '80€',
            duration: '3h',
            category: 'soin'
        },
        {
            id: 7,
            icon: FaTint,
            name: 'Permanente & Boucles',
            description: 'Boucles durables et naturelles',
            price: '70€',
            duration: '2h30',
            category: 'coupe'
        },
        {
            id: 8,
            icon: FaStar,
            name: 'Traitement Cuir Chevelu',
            description: 'Soin spécialisé pour cuir chevelu sensible',
            price: '35€',
            duration: '45 min',
            category: 'soin'
        },
    ];

    const benefits = [
        {
            icon: FaHome,
            title: 'À votre domicile',
            description: 'Pas de déplacement, confort maximal dans votre environnement',
        },
        {
            icon: FaClock,
            title: 'Flexible',
            description: 'Horaires adaptés à votre emploi du temps, même le week-end',
        },
        {
            icon: FaCrown,
            title: 'Premium',
            description: 'Service haut de gamme avec produits professionnels',
        },
        {
            icon: FaCheckCircle,
            title: 'Professionnel',
            description: 'Coiffeur qualifié avec 10+ ans d\'expérience',
        },
        {
            icon: FaDollarSign,
            title: 'Tarifs Justes',
            description: 'Meilleur rapport qualité-prix du marché',
        },
        {
            icon: FaLock,
            title: 'Confiance',
            description: 'Discrétion garantie et respect de votre intimité',
        },
    ];

    const testimonials = [
        {
            name: 'Mishiku Gertrude',
            role: 'Commerçante',
            text: 'Service impeccable! Je gagne du temps et les résultats sont excellents. Je recommande vivement!',
            rating: 5,
            initials: 'MG'
        },
        {
            name: 'Clara Ihemba',
            role: 'Mère de famille',
            text: 'Enfin un service qui s\'adapte à mon emploi du temps chargé. OV Hairstyle est devenu indispensable!',
            rating: 5,
            initials: 'CI'
        },
        {
            name: 'Ketia Ngoma',
            role: 'Entrepreneur',
            text: 'Professionnalisme et qualité au rendez-vous. Les produits utilisés sont de très bonne qualité.',
            rating: 5,
            initials: 'KN'
        },
    ];

    const steps = [
        {
            number: 1,
            title: 'Réservez',
            description: 'Choisissez votre service et vos horaires via WhatsApp ou notre plateforme',
            icon: FaCalendarAlt
        },
        {
            number: 2,
            title: 'Confirmez',
            description: 'Recevez une confirmation et un rappel 24h avant votre rendez-vous',
            icon: FaCheckCircle
        },
        {
            number: 3,
            title: 'Détendez-vous',
            description: 'Notre coiffeur vient chez vous avec tout le matériel nécessaire',
            icon: FaHome
        },
        {
            number: 4,
            title: 'Profitez',
            description: 'Payez et profitez de votre nouveau look sans stress',
            icon: FaStar
        },
    ];

    // MODIFIÉ: Mémoriser galleryImages avec useMemo
    const galleryImages = useMemo(() => [
        {
            id: 1,
            src: '/images/gallery/gallery-1.jpeg',
            alt: 'Coupe moderne et élégante'
        },
        {
            id: 2,
            src: '/images/gallery/gallery-2.jpeg',
            alt: 'Coloration éclatante'
        },
        {
            id: 3,
            src: '/images/gallery/gallery-3.jpeg',
            alt: 'Balayage lumineux'
        },
        {
            id: 4,
            src: '/images/gallery/gallery-4.jpeg',
            alt: 'Coiffure événementielle'
        },
        {
            id: 5,
            src: '/images/gallery/gallery-5.jpg',
            alt: 'Lissage brésilien'
        },
        {
            id: 6,
            src: '/images/gallery/gallery-6.jpg',
            alt: 'Traitement capillaire'
        },
    ], []);

    const filteredServices = activeTab === 'all'
        ? services
        : services.filter(s => s.category === activeTab);

    // Fonctions pour la modal
    const selectedImage = galleryImages.find(img => img.id === selectedImageId);
    const selectedIndex = galleryImages.findIndex(img => img.id === selectedImageId);

    // MODIFIÉ: useCallback avec galleryImages mémorisé
    const handlePrevImage = useCallback(() => {
        const newIndex = selectedIndex === 0 ? galleryImages.length - 1 : selectedIndex - 1;
        setSelectedImageId(galleryImages[newIndex].id);
    }, [selectedIndex, galleryImages]);

    const handleNextImage = useCallback(() => {
        const newIndex = selectedIndex === galleryImages.length - 1 ? 0 : selectedIndex + 1;
        setSelectedImageId(galleryImages[newIndex].id);
    }, [selectedIndex, galleryImages]);

    const handleCloseModal = useCallback(() => {
        setSelectedImageId(null);
    }, []);

    // CORRIGÉ: Dépendances complètes
    React.useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') handleCloseModal();
            if (e.key === 'ArrowLeft') handlePrevImage();
            if (e.key === 'ArrowRight') handleNextImage();
        };

        if (selectedImageId) {
            window.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'auto';
        };
    }, [selectedImageId, handleCloseModal, handlePrevImage, handleNextImage]);

    return (
        <>
            <SEO
                title="OV Hairstyle - Salon de Coiffure à Domicile | Kinshasa"
                description="Service de coiffure premium à domicile à Kinshasa. Coupes, colorations, soins capillaires. Réservez votre rendez-vous en ligne."
                url="https://ov-hairstyle.vercel.app"
                image="https://ov-hairstyle.vercel.app/images/og-image.jpeg"
            />

            <Layout>
                {/* Hero Section */}
                <section className={styles.hero} id="hero">
                    <div className={styles.heroBackground}>
                        <div className={styles.floatingElement1}></div>
                        <div className={styles.floatingElement2}></div>
                    </div>

                    <div className={styles.heroContent}>
                        <div className={styles.heroBadge}>
                            <FaStar className={styles.badgeIcon} /> Service Premium à domicile
                        </div>

                        <div className={styles.logoHero}>
                            <img src="/images/og-image.jpeg" alt="OV Hairstyle" />
                        </div>

                        <h1 className={styles.heroTitle}>
                            Transformez votre look<br />
                            <span className={styles.highlight}>sans quitter votre domicile</span>
                        </h1>

                        <p className={styles.heroSubtitle}>
                            L'expertise d'un salon professionnel, le confort de votre maison.<br />
                            Réservez votre rendez-vous en quelques clics  !
                        </p>

                        <div className={styles.heroStats}>
                            <div className={styles.stat}>
                                <span className={styles.statNumber}>100+</span>
                                <span className={styles.statLabel}>Clients satisfaits</span>
                            </div>
                            <div className={styles.stat}>
                                <span className={styles.statNumber}>4.9★</span>
                                <span className={styles.statLabel}>Note moyenne</span>
                            </div>
                            <div className={styles.stat}>
                                <span className={styles.statNumber}>2+</span>
                                <span className={styles.statLabel}>Ans d'expérience</span>
                            </div>
                        </div>

                        <div className={styles.heroButtons}>
                            <Link to="/reservation">
                                <Button size="large" className={styles.primaryBtn}>
                                    <FaCalendarAlt className={styles.btnIcon} /> Réserver Maintenant
                                </Button>
                            </Link>

                            <Button variant="secondary" size="large" onClick={() => scrollToSection('services')}>
                                <FaPaintBrush className={styles.btnIcon} /> Voir Nos Services
                            </Button>
                        </div>

                        <div className={styles.heroContact}>
                            <p>Besoin d'aide?  Contactez-nous sur WhatsApp</p>
                            <a href="https://wa.me/243972315704" target="_blank" rel="noreferrer" className={styles.whatsappLink}>
                                <BsWhatsapp className={styles.whatsappIcon} /> Discuter avec nous
                            </a>
                        </div>
                    </div>
                </section>

                {/* Services Section */}
                <section className={styles.servicesSection} id="services">
                    <div className={styles.container}>
                        <div className={styles.sectionHeader}>
                            <h2>Nos services Premium</h2>
                            <p>Découvrez notre gamme complète de services de coiffure</p>
                        </div>

                        <div className={styles.filters}>
                            <button
                                className={`${styles.filterBtn} ${activeTab === 'all' ? styles.active : ''}`}
                                onClick={() => setActiveTab('all')}
                            >
                                Tous les services
                            </button>
                            <button
                                className={`${styles.filterBtn} ${activeTab === 'coupe' ? styles.active : ''}`}
                                onClick={() => setActiveTab('coupe')}
                            >
                                <FaCut /> Coupes
                            </button>
                            <button
                                className={`${styles.filterBtn} ${activeTab === 'couleur' ? styles.active : ''}`}
                                onClick={() => setActiveTab('couleur')}
                            >
                                <FaPaintBrush /> Couleurs
                            </button>
                            <button
                                className={`${styles.filterBtn} ${activeTab === 'soin' ? styles.active : ''}`}
                                onClick={() => setActiveTab('soin')}
                            >
                                <FaTint /> Soins
                            </button>
                            <button
                                className={`${styles.filterBtn} ${activeTab === 'event' ? styles.active : ''}`}
                                onClick={() => setActiveTab('event')}
                            >
                                <FaCrown /> Événements
                            </button>
                        </div>

                        <div className={styles.servicesGrid}>
                            {filteredServices.map((service) => {
                                const IconComponent = service.icon;
                                return (
                                    <div key={service.id} className={styles.serviceCardWrapper}>
                                        <Card hover>
                                            <div className={styles.serviceCard}>
                                                <div className={styles.serviceIcon}>
                                                    <IconComponent />
                                                </div>
                                                <h3>{service.name}</h3>
                                                <p>{service.description}</p>
                                                <div className={styles.serviceMeta}>
                                                    <span className={styles.duration}>
                                                        <FaClock /> {service.duration}
                                                    </span>
                                                </div>
                                                <div className={styles.serviceFooter}>
                                                    <div className={styles.servicePrice}>{service.price}</div>
                                                    <Link to="/reservation">
                                                        <Button size="small">Réserver</Button>
                                                    </Link>
                                                </div>
                                            </div>
                                        </Card>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Processus Section */}
                <section className={styles.processSection} id="process">
                    <div className={styles.container}>
                        <div className={styles.sectionHeader}>
                            <h2>Comment ça marche?</h2>
                            <p>4 étapes simples pour un rendez-vous parfait</p>
                        </div>

                        <div className={styles.stepsGrid}>
                            {steps.map((step, idx) => {
                                const IconComponent = step.icon;
                                return (
                                    <div key={idx} className={styles.stepCard}>
                                        <div className={styles.stepNumber}>{step.number}</div>
                                        <div className={styles.stepIcon}>
                                            <IconComponent />
                                        </div>
                                        <h3>{step.title}</h3>
                                        <p>{step.description}</p>
                                        {idx < steps.length - 1 && (
                                            <div className={styles.stepArrow}>
                                                <FaChevronRight />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Avantages Section */}
                <section className={styles.benefitsSection} id="benefits">
                    <div className={styles.container}>
                        <div className={styles.sectionHeader}>
                            <h2>Pourquoi Choisir OV Hairstyle?</h2>
                            <p>Les avantages qui font la différence</p>
                        </div>

                        <div className={styles.benefitsGrid}>
                            {benefits.map((benefit, idx) => {
                                const IconComponent = benefit.icon;
                                return (
                                    <div key={idx} className={styles.benefitCard}>
                                        <div className={styles.benefitIcon}>
                                            <IconComponent />
                                        </div>
                                        <h3>{benefit.title}</h3>
                                        <p>{benefit.description}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Témoignages Section */}
                <section className={styles.testimonialsSection} id="testimonials">
                    <div className={styles.container}>
                        <div className={styles.sectionHeader}>
                            <h2>Ce que disent nos clients</h2>
                            <p>Découvrez les avis de nos clients satisfaits</p>
                        </div>

                        <div className={styles.testimonialsGrid}>
                            {testimonials.map((testimonial, idx) => (
                                <Card key={idx} hover>
                                    <div className={styles.testimonialCard}>
                                        <div className={styles.testimonialHeader}>
                                            <div className={styles.testimonialAvatar}>
                                                {testimonial.initials}
                                            </div>
                                            <div>
                                                <h4>{testimonial.name}</h4>
                                                <p className={styles.testimonialRole}>{testimonial.role}</p>
                                            </div>
                                        </div>
                                        <div className={styles.testimonialRating}>
                                            {Array(testimonial.rating).fill(0).map((_, i) => (
                                                <FaStar key={i} />
                                            ))}
                                        </div>
                                        <div className={styles.testimonialQuote}>
                                            <FaQuoteLeft />
                                        </div>
                                        <p className={styles.testimonialText}>{testimonial.text}</p>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Galerie Section */}
                <section className={styles.gallerySection} id="gallery">
                    <div className={styles.container}>
                        <div className={styles.sectionHeader}>
                            <h2>Notre galerie</h2>
                            <p>Découvrez nos réalisations</p>
                        </div>

                        <div className={styles.galleryGrid}>
                            {galleryImages.map((image) => (
                                <div
                                    key={image.id}
                                    className={styles.galleryItem}
                                    onClick={() => setSelectedImageId(image.id)}
                                >
                                    <img
                                        src={image.src}
                                        alt={image.alt}
                                        className={styles.galleryImage}
                                        loading="lazy"
                                        onError={(e) => {
                                            console.error(`Erreur chargement image: ${image.src}`);
                                            e.target.style.display = 'none';
                                            if (e.target.nextElementSibling) {
                                                e.target.nextElementSibling.style.display = 'flex';
                                            }
                                        }}
                                        onLoad={() => {
                                            // console.log(`Image chargée: ${image.src}`);
                                        }}
                                    />
                                    <div className={styles.galleryPlaceholder} style={{ display: 'none' }}>
                                        <FaCamera />
                                        <span>{image.alt}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* MODAL GALERIE */}
                {selectedImageId && (
                    <div className={styles.modalOverlay} onClick={handleCloseModal}>
                        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                            {/* Bouton Fermer */}
                            <button
                                className={styles.modalClose}
                                onClick={handleCloseModal}
                                aria-label="Fermer"
                            >
                                <FaTimes />
                            </button>

                            {/* Image */}
                            <div className={styles.modalImageContainer}>
                                <img
                                    src={selectedImage.src}
                                    alt={selectedImage.alt}
                                    className={styles.modalImage}
                                />
                            </div>

                            {/* Description */}
                            <div className={styles.modalInfo}>
                                <p className={styles.modalAlt}>{selectedImage.alt}</p>
                                <p className={styles.modalCounter}>
                                    {selectedIndex + 1} / {galleryImages.length}
                                </p>
                            </div>

                            {/* Boutons Navigation */}
                            <button
                                className={styles.modalPrev}
                                onClick={handlePrevImage}
                                aria-label="Image précédente"
                            >
                                <FaChevronLeft />
                            </button>

                            <button
                                className={styles.modalNext}
                                onClick={handleNextImage}
                                aria-label="Image suivante"
                            >
                                <FaArrowRight />
                            </button>

                            {/* Indicateurs */}
                            <div className={styles.modalIndicators}>
                                {galleryImages.map((img) => (
                                    <button
                                        key={img.id}
                                        className={`${styles.indicator} ${img.id === selectedImageId ? styles.active : ''}`}
                                        onClick={() => setSelectedImageId(img.id)}
                                        aria-label={`Aller à l'image ${img.id}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* FAQ Section */}
                <section className={styles.faqSection} id="faq">
                    <div className={styles.container}>
                        <div className={styles.sectionHeader}>
                            <h2>Questions fréquemment posées</h2>
                            <p>Trouvez les réponses à vos questions</p>
                        </div>

                        <div className={styles.faqGrid}>
                            <div className={styles.faqItem}>
                                <h4>
                                    <FaDollarSign /> Quel est le coût du déplacement?
                                </h4>
                                <p>Le déplacement est gratuit pour les clients situés dans un rayon de 1km. Au-delà, un supplément de 5€ par km supplémentaire est appliqué.</p>
                            </div>
                            <div className={styles.faqItem}>
                                <h4>
                                    <FaClock /> Quels sont vos horaires?
                                </h4>
                                <p>Nous sommes disponibles du lundi au samedi, de 9h à 18h. Les rendez-vous le dimanche sont possibles sur demande.</p>
                            </div>
                            <div className={styles.faqItem}>
                                <h4>
                                    <FaDollarSign /> Quels modes de paiement acceptez-vous?
                                </h4>
                                <p>Nous acceptons les espèces, les cartes bancaires, les virements et les paiements par mobile money.</p>
                            </div>
                            <div className={styles.faqItem}>
                                <h4>
                                    <FaCheckCircle /> Comment annuler un rendez-vous?
                                </h4>
                                <p>Vous pouvez annuler gratuitement jusqu'à 24h avant. Au-delà, des frais de 50% s'appliquent.</p>
                            </div>
                            <div className={styles.faqItem}>
                                <h4>
                                    <FaLock /> Utilisez-vous des produits hypoallergéniques?
                                </h4>
                                <p>Oui! Nous utilisons des produits professionnels de qualité. Informez-nous de vos allergies lors de la réservation.</p>
                            </div>
                            <div className={styles.faqItem}>
                                <h4>
                                    <FaHome /> Livrez-vous en dehors de Kinshasa?
                                </h4>
                                <p>Actuellement, nous opérons uniquement à Kinshasa. Contactez-nous pour connaître nos plans d'expansion.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Final Section */}
                <section className={styles.ctaFinalSection} id="cta">
                    <div className={styles.container}>
                        <div className={styles.ctaContent}>
                            <h2>Prêt à transformer votre look?</h2>
                            <p>Réservez votre rendez-vous dès maintenant et profitez d'un service premium</p>
                            <div className={styles.ctaButtons}>
                                <Link to="/reservation">
                                    <Button size="large">
                                        <FaCalendarAlt className={styles.btnIcon} /> Réserver Maintenant
                                    </Button>
                                </Link>
                                <a href="https://wa.me/243972315704" target="_blank" rel="noreferrer" className={styles.ctaWhatsappBtn}>
                                    <BsWhatsapp className={styles.whatsappBtnIcon} />
                                    <span>Nous Contacter sur WhatsApp</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            </Layout>
        </>
    );
}
